import { inject as service } from '@ember/service';
import Controller from '@ember/controller';
import { task } from 'ember-concurrency';
import { CampaignType } from 'cio/models/campaign';
import { NEWSLETTERS } from 'cio/utils/broadcasts';
import { isEmpty } from '@ember/utils';
import FormValidations from 'cio/utils/form-validations';
import EmObject, { set, action } from '@ember/object';

import ApiErrorsService from 'cio/services/api-errors';
import AnalyticsService from 'cio/services/analytics';
import NewsletterModel from 'cio/models/newsletter';
import TagModel from 'cio/models/tag';
import DS from 'ember-data';
import RouterService from '@ember/routing/router-service';

const Validations = {
  name(value: string) {
    if (isEmpty(value) || isEmpty(value.trim())) {
      return "Name can't be blank";
    }

    return true;
  },

  selectedType(value: string) {
    if (isEmpty(value)) {
      return 'Select a broadcast type';
    }

    return true;
  },
};

const TRIGGER_TYPES = [
  {
    id: NEWSLETTERS,
    icon: 'broadcast',
    label: 'Newsletter',
    description:
      'Send a single message for communication such as product updates, terms-of-service or policy changes, or targeted one-time promotions.',
  },
  {
    id: CampaignType.APITriggered,
    icon: 'api-triggered-broadcast',
    label: 'Messages triggered via API',
    description:
      'Send messages to groups of people, such as topic-specific alerts, event reminders, or multi-language announcements.',
  },
];

const DEFAULT_FORM_VALUES: { name: string | null; selectedType: string | null } = {
  name: null,
  selectedType: null,
};

export default class NewController extends Controller {
  validations!: FormValidations;

  @service analytics!: AnalyticsService;
  @service apiErrors!: ApiErrorsService;
  @service store!: DS.Store;
  @service router!: RouterService;

  // @ts-ignore - not accepting array of objects, invalid Ember typings for this case?
  queryParams = [
    {
      '_formValues.selectedType': 'type',
    },
  ];

  TRIGGER_TYPES = TRIGGER_TYPES;

  // internal state
  _formValues = DEFAULT_FORM_VALUES;
  tags: TagModel[] = [];

  _hasSubmitted = false;

  constructor() {
    super(...arguments);

    this.set(
      'validations',
      FormValidations.create({ validations: Validations, context: this, modelKey: '_formValues' }),
    );
  }

  _createNewsletter = task({ drop: true }, async () => {
    // Channel is set to "unset" or it will default to email. This is in-place to make the old UI compatable with the BE until after launch of
    // non-email newsletters
    let newsletter = this.store.createRecord('newsletter', { channel: 'unset' });

    newsletter.setProperties({
      name: this._formValues.name,
      tags: this.tags,
    });

    try {
      await newsletter.save();

      // update setup guide "created campaign" step if not completed already
      // non-critical operation: launched in the background
      this._updateSetupGuideCreatedCampaignStep(newsletter);
      this.router.transitionTo('broadcasts.newsletter.setup', newsletter);
    } catch (response) {
      newsletter.unloadRecord();
      this.apiErrors.report(response);
    }
  });

  _createApiTriggeredBroadcast = task({ drop: true }, async () => {
    let campaign = this.store.createRecord('campaign');

    campaign.setProperties({
      name: this._formValues.name,
      tags: this.tags,
      type: CampaignType.APITriggered,
    });

    try {
      await campaign.save();
      this.router.transitionTo('campaigns.campaign.setup', campaign);
    } catch (response) {
      campaign.unloadRecord();
      this.apiErrors.report(response);
    }
  });

  _createTask = task({ drop: true }, async (ev: Event) => {
    ev.preventDefault();

    this.set('_hasSubmitted', true);
    try {
      await this.validations.validateModel();
    } catch (e) {
      // `validateModel` already found and set validation errors
      // return early to avoid executing real submit logic
      return;
    }

    if (this._formValues.selectedType === CampaignType.APITriggered) {
      await this._createApiTriggeredBroadcast.perform();
    } else {
      await this._createNewsletter.perform();
    }
  });

  async _updateSetupGuideCreatedCampaignStep(newsletter: NewsletterModel) {
    let setupGuide = await this.store.findRecord('setup-guide', 'current');

    if (setupGuide.completedSteps['create-campaign']) {
      return;
    }

    await setupGuide.assignFirstMessage(newsletter);
    this.analytics.track('broadcast', 'create_started', { source: 'Broadcasts' });
  }

  setDefaults() {
    this.setProperties({
      _formValues: EmObject.create({ ...DEFAULT_FORM_VALUES }),
      _hasSubmitted: false,
      tags: [],
    });
  }

  @action
  addTag(tag: TagModel) {
    return this.tags.addObject(tag);
  }

  @action
  removeTag(tag: TagModel) {
    return this.tags.removeObject(tag);
  }

  @action
  selectType(id: string) {
    set(this._formValues, 'selectedType', id);
    this.validations.validateAttribute('selectedType');
  }

  @action
  cancel() {
    this.router.transitionTo('broadcasts');
  }

  @action
  onNameChange(name: string) {
    set(this._formValues, 'name', name);
    this.validations.validateAttribute('name');
  }
}
